import {useRouter} from "vue-router";
import ApiService, {SerialApi, SerialStatusEnum, UserConsumer, UserStatusEnum} from "@/api/apiService";
import {inject, ref} from "vue";
import useModal from "@/hooks/Modal";
import {formatDate} from "@/utils/general-utils";
import {viewLog} from "@/utils/Logs";

export interface TableCustomer {
  id: string;
  serials: SerialApi[];
  language: string;
  status: TableStatus;
  lastRegisterDate: string;
  connectionsNumber: number;
  email: string | null;
  phoneNumber: string | null;
  consumer: UserConsumer;
  gigyaId: string | null;
}

export interface TableStatus {
  user: UserStatusEnum;
  strongerSerial: SerialStatusEnum;
}

export function useUI() {

  const router = useRouter();

  function isCustomerRed(status: string): boolean {
    return status === UserStatusEnum.SUSPICIOUS;
  }

  function isCustomerOrange(status: string): boolean {
    return status === SerialStatusEnum.NOT_SOLD;
  }

  function redirectToCustomer(customerId: string) {
    router.push({name: 'customer-file-page', params: {id: customerId}});
  }

  return {
    isCustomerRed,
    isCustomerOrange,
    redirectToCustomer,
  };
}

export function useDownloadData() {
  const isDownloadingData = ref(false);
  const downloadTypeRadioRef = ref('event');

  const {currentModal, openModal, closeModal} = useModal();
  const modals = {
    DOWNLOAD_EVENT_LOGS: 'download-event-logs',
  };

  const tomorrow = new Date();
  tomorrow.setDate(new Date().getDate() + 1);

  const logsDatePickerStart = ref(new Date());
  const logsDatePickerEnd = ref(tomorrow);

  function getDateParams(): string {
    // @ts-ignore https://github.com/icehaunter/vue3-datepicker/issues/65
    return `?itemsPerPage=500&creationDate[before]=${formatDate(logsDatePickerEnd.value)}&creationDate[after]=${formatDate(logsDatePickerStart.value)}`;
  }

  function openDownloadData() {
    openModal(modals.DOWNLOAD_EVENT_LOGS);
  }

  async function downloadData() {
    let response;
    isDownloadingData.value = true;

    if (downloadTypeRadioRef.value === 'event') {
      response = await ApiService.getEventLogs([getDateParams()]);
    } else if (downloadTypeRadioRef.value === 'register') {
      response = await ApiService.getRegisterLogs([getDateParams()]);
    } else if (downloadTypeRadioRef.value === 'deletion') {
      response = await ApiService.getEventLogs([getDateParams() + '&only-deletions']);
    }

    if (!response) {
      isDownloadingData.value = false;
      return;
    }

    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(response.csv));
    element.setAttribute('download', response.filename);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);

    isDownloadingData.value = false;
  }

  return {
    isDownloadingData,
    downloadData,
    openDownloadData,
    modals,
    currentModal,
    closeModal,
    logsDatePickerStart,
    logsDatePickerEnd,
    downloadTypeRadioRef,
  }
}

export function useUpdateAEM() {
  const isFlushingAEM = ref(false);
  const toast: any = inject('toast');

  async function flushAEM(forceClearCache: boolean) {
    isFlushingAEM.value = true;
    try {
      await ApiService.startUpdateTranslations(forceClearCache);
    } catch (error) {
      toast.error("An error occured");
      viewLog.debug(error);
    }
    isFlushingAEM.value = false;
  }

  return {
    isFlushingAEM,
    flushAEM,
  }
}
